<script setup lang="ts">
import { inject, onMounted, useTemplateRef, watch } from 'vue';
import { gripFieldHeight, MODAL_RESIZE_DRIVER } from './modalResize';

/**
 * A resize grip for a field inside a resizable `NxModal` — the replacement for a native
 * CSS `resize` on a text field, which grows the field alone and, in an auto-sized dialog, makes
 * the centred dialog run away from the pointer.
 *
 * Put it into a `position: relative`, flex-column box around the field; it sits in that box's
 * bottom-right corner. Dragging it resizes the MODAL through the modal's own resize path (the
 * corner handle's start / move / end): width and height follow the pointer, the top-left corner
 * stays put, the window edge and the natural size stop it, and the size is remembered under the
 * modal's `storageKey`. A maximized dialog leaves the maximized state, as with an edge.
 *
 * The field fills the modal (I-2563): `v-model:height` is the box's own height plus all the
 * height the modal has over its natural size (`gripFieldHeight`) — while the grip drags, after
 * an edge drag, when a remembered size opens, when maximized. Bind it as the box's `min-height`
 * and let the field fill the box (a kit `NxEditorInput` with `fill`). It is `null` while the
 * modal is at its natural height. Outside a resizable modal there is no grip.
 *
 * The box's own height is measured as the box lays out WITHOUT stretching (`flex: 0 0 auto`, no
 * `min-height`): a box that grows into the modal's room (`flex: 1 0 auto`) would otherwise count
 * the modal's growth twice, and the field could not shrink with the modal again. A grip that
 * mounts into an already grown modal (the body loads after the open) takes its share at once.
 */
const height = defineModel<number | null>('height', { default: null });

const driver = inject(MODAL_RESIZE_DRIVER, null);
const gripRef = useTemplateRef<HTMLElement>('grip');
let dragging = false;

/**
 * The height the box lays out at on its own: measured with its stretch and its `min-height`
 * (what this grip sets) switched off for the moment, restored before anything is painted.
 */
function ownHeight(box: HTMLElement): number {
  const { flex, minHeight } = box.style;
  box.style.flex = '0 0 auto';
  box.style.minHeight = '';
  const own = box.offsetHeight;
  box.style.flex = flex;
  box.style.minHeight = minHeight;
  return own;
}

/** The field box's height for the modal's current growth; `null` when nothing is to add. */
function fieldHeight(growth: number): number | null {
  const box = gripRef.value?.parentElement;
  if (growth <= 0 || !box) return null;
  return gripFieldHeight(ownHeight(box), growth);
}

// The body (and this grip) mounts with every open: each open starts at the field's own height.
watch(
  () => driver?.isOpen() ?? false,
  (open) => {
    if (open) height.value = null;
  },
  { immediate: true }
);

// Whatever makes the modal taller than its natural size goes to the field.
watch(
  () => driver?.growth() ?? 0,
  (growth) => {
    height.value = fieldHeight(growth);
  },
  { flush: 'post' }
);

// A grip that mounts after the modal has grown (a body that loads once the dialog is open) sees
// no change of the growth: it takes its share here, the box being in the document now.
onMounted(() => {
  height.value = fieldHeight(driver?.growth() ?? 0);
});

function onPointerDown(e: PointerEvent) {
  dragging = !!driver?.start(e, 'se');
}

function onPointerMove(e: PointerEvent) {
  if (dragging) driver?.move(e);
}

function onPointerUp(e: PointerEvent) {
  if (!dragging) return;
  dragging = false;
  driver?.end(e);
}
</script>

<template>
  <!-- A span, so it may sit inside a label around the field. -->
  <span
    v-if="driver?.enabled()"
    ref="grip"
    class="nx-modal-resize-grip"
    data-test="modal-resize-grip"
    data-no-drag
    aria-hidden="true"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
  ></span>
</template>

<style scoped>
/* Looks like the modal's corner handle: a diagonal grip. */
.nx-modal-resize-grip {
  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 1;
  width: var(--nxt-space-md);
  height: var(--nxt-space-md);
  cursor: nwse-resize;
  touch-action: none;
  background: linear-gradient(
    135deg,
    transparent 55%,
    var(--nxt-border-strong) 55%,
    var(--nxt-border-strong) 65%,
    transparent 65%,
    transparent 80%,
    var(--nxt-border-strong) 80%,
    var(--nxt-border-strong) 90%,
    transparent 90%
  );
}
</style>
